/* eslint-disable import/prefer-default-export */
import { defineStore } from 'pinia';
import { ref } from 'vue';
import MetamaskCryptoPayment from '@/composables/metamask/metamaskPayment';
import {
  getInstalledMetamaskProvider,
  requestChangeNetwork,
} from '@/composables/metamask/useMetamaskProvider';
import useLocalStorage from '@/composables/storage/useLocalStorage';
import TransactionWatcher from '@/composables/metamask/transactionWatcher';
import UserTransferCoin from '@/composables/metamask/userTransferCoin';

const { bindLocalStorage } = useLocalStorage();

const transactionHash = ref('');
const order = ref({
  coinType: '',
  coinAmount: '',
  orderKey: '',
});
const recipientWalletAddress = ref('');
const userWalletAddress = ref('');
const isWalletConnected = ref(false);
const userBalance = ref('');
const evmChainId = ref('');
const tokenAddress = ref('');
const isGettingUserBalance = ref(false);
const connectedChainId = ref('');

let transactionWatcher;

export const TransactionStatus = {
  INIT: 'init',
  PENDING: 'pending',
  SUCCESS: 'success',
  FAILED: 'failed',
};

export const useMetamaskDepositFormStore = defineStore({
  id: 'metamaskDeposit',
  state: () => ({
    transactionHash,
    userBalance,
    order,
    recipientWalletAddress,
    userWalletAddress,
    evmChainId,
    isWalletConnected,
    tokenAddress,
    isGettingUserBalance,
    transactionStatus: JSON.parse(localStorage.getItem('sellTransactionStatus')) ?? TransactionStatus.INIT,
    connectedChainId,
    }),
    getters: {
        getIsSufficientBalance(store) {
            return parseFloat(store.userBalance) >= parseFloat(store.order.coinAmount);
    },
    getIsWalletConnected(store) {
      return store.isWalletConnected;
    },
    getChainId(store) {
      return store.evmChainId;
    },
    getIsGettingUserBalance(store) {
      return store.isGettingUserBalance;
    },
    getIsCorrectNetwork(store) {
      return store.evmChainId === store.connectedChainId;
    },
  },
  actions: {
    async initEthersWatcher() {
      const mmProvider = await getInstalledMetamaskProvider();
      if (mmProvider.chainId && mmProvider.chainId !== '') {
        this.connectedChainId = mmProvider.chainId;
      }
      mmProvider.on('accountsChanged', ([account]) => {
        if (account === undefined) {
          // Disconnect
          this.setIsWalletConnected(false);
          return;
        }
        this.userWalletAddress = account;
        this.userBalance = 0;
        this.getUserBalance();
      });
      mmProvider.on('connect', async (connectInfo) => {
        this.connectedChainId = connectInfo.chainId;
      });
      mmProvider.on('chainChanged', async (chainId) => {
        if (chainId) {
          this.connectedChainId = chainId;
        }
        if (chainId !== this.evmChainId) {
          requestChangeNetwork(this.evmChainId);
          return;
        }
        this.getUserBalance();
      });
    },
    async removeEthersWatcher() {
      const mmProvider = await getInstalledMetamaskProvider();
      mmProvider.removeAllListeners('accountsChanged');
      mmProvider.removeAllListeners('chainChanged');
    },
    // This function will initiate the transaction watcher,
    // which will monitor mined transaction related to this order
    async watchTransaction() {
      transactionWatcher = new TransactionWatcher(
        this.order.coinType,
        this.recipientWalletAddress,
        this.order.orderKey,
        this.evmChainId,
        this.tokenAddress,
      );
      transactionWatcher.watchTransaction((sourceAddress, _transactionHash) => {
        if (this.transactionHash !== _transactionHash) {
          this.userWalletAddress = sourceAddress;
          this.transactionHash = _transactionHash;
        }
      });
    },
    removeTransactionWatcher() {
      transactionWatcher.unwatchTransaction();
    },
    setUserAddress(_userWalletAddress) {
      this.userWalletAddress = _userWalletAddress;
    },
    setIsWalletConnected(_isWalletConnected) {
      if (_isWalletConnected) {
        this.initEthersWatcher();
      } else {
        this.removeEthersWatcher();
      }
      this.isWalletConnected = _isWalletConnected;
    },
    setOrderData(coinType, amount, orderKey) {
      this.order.coinAmount = amount;
      this.order.coinType = coinType;
      this.order.orderKey = orderKey;
    },
    setRecipientWalletAddress(address) {
      this.recipientWalletAddress = address;
    },
    setChain(_evmChainId) {
      this.evmChainId = `0x${_evmChainId.toString(16)}`;
    },
    setTokenAddress(_tokenAddress) {
      this.tokenAddress = _tokenAddress;
    },
    setTransactionStatus(_transactionStatus) {
      this.transactionStatus = _transactionStatus;
      bindLocalStorage('sellTransactionStatus', this.transactionStatus);
    },
    startGetUserBalance() {
      if (this.isWalletConnected) {
        this.getUserBalance();
      }
      setTimeout(() => {
        this.startGetUserBalance();
      }, 60000);
    },
    async getUserBalance() {
      if (parseFloat(this.userBalance) < parseFloat(this.order.coinAmount)) {
        this.isGettingUserBalance = true;
      }
      this.userBalance = await MetamaskCryptoPayment.getUserBalance(
        this.evmChainId,
        this.order.coinType,
        this.tokenAddress,
        this.userWalletAddress,
      );
      this.isGettingUserBalance = false;
    },
    async submitTokenTransferRequest() {

      const transferCoin = new UserTransferCoin(
        this.evmChainId,
        this.order.coinType,
        this.recipientWalletAddress,
        this.order.coinAmount.toString(),
        this.tokenAddress,
        this.order.orderKey,
      );
      const metamaskTransaction = await transferCoin.transfer();

      if (!metamaskTransaction) {
        this.transactionStatus = TransactionStatus.INIT;
        bindLocalStorage('sellTransactionStatus', this.transactionStatus);
        throw new Error('Failed to send transaction');
      }
      this.transactionHash = metamaskTransaction;
    },
  },
});
